import { colors, date, intl, icons, ui } from '@beelday/common'
import styled from '@emotion/styled'
import { FC } from 'react'
import { StageControls } from 'room/common/stage-surface'
import { Activity } from './script-model'

const Container = styled.div`
	background: ${colors.white};
	border: 1px solid ${colors.borderLightGray};
	border-radius: 14px;
	overflow: hidden;
`

const Header = styled.div`
	padding: 18px 24px 14px;
	border-bottom: 1px solid ${colors.borderLightGray};
`

const Eyebrow = styled.div`
	font-size: 11px;
	font-weight: 600;
	color: ${colors.heather};
	text-transform: uppercase;
	letter-spacing: 0.4px;
	margin-bottom: 3px;
`

const Title = styled.div`
	font-size: 18px;
	font-weight: 700;
	color: ${colors.black};
`

const DayTag = styled.div`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	background: ${colors.black};
	color: ${colors.white};
	font-size: 11px;
	font-weight: 600;
	padding: 5px 12px 5px 10px;
	border-radius: 20px;
	margin: 16px 0 0 24px;

	svg {
		width: 12px;
		height: 12px;
	}
`

const Body = styled.div`
	padding: 20px 24px 28px;
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
`

const IconBadge = styled.div`
	width: 52px;
	height: 52px;
	border-radius: 14px;
	background: ${colors.indigoBlue10};
	color: ${colors.indigoBlue};
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 16px;

	svg {
		width: 26px;
		height: 26px;
	}
`

const ElementName = styled.div`
	font-size: 12px;
	color: ${colors.heather};
	margin-bottom: 4px;
`

const RepresentativeText = styled.div`
	font-size: 16px;
	font-weight: 600;
	color: ${colors.black};
	margin-bottom: 8px;
	max-width: 460px;
	line-height: 1.4;
`

const Description = styled.div`
	font-size: 13px;
	color: ${colors.heather};
	max-width: 420px;
	line-height: 1.6;
	margin-bottom: 20px;
`

type PollingToolType =
	| 'POLLING_OPEN_ENDED_WORD_CLOUD'
	| 'POLLING_OPEN_ENDED_LIST'
	| 'POLLING_MULTIPLE_CHOICE'

type Props = {
	title: string
	toolType: Activity['type'] | PollingToolType
	representativeQuestion?: string
	day?: { index: number; startDate: Date }
	isTrainer: boolean
	onSeeInRecaps?: () => void
}

export const ArchivedActivityCard: FC<Props> = ({
	title,
	toolType,
	representativeQuestion,
	day,
	isTrainer,
	onSeeInRecaps,
}) => {
	const translate = intl.useTranslate()
	const dateFormat = date.useDateFormat()

	return (
		<Container>
			<Header>
				<Eyebrow>
					<intl.Translate>script.archived.eyebrow</intl.Translate>
				</Eyebrow>
				<Title>{title}</Title>
			</Header>
			{day ? (
				<DayTag>
					<icons.Time />
					{translate('script.archived.day_label')} {day.index + 1} •{' '}
					{dateFormat.formatDate(day.startDate, 'SHORT')}
				</DayTag>
			) : null}
			<Body>
				<IconBadge>
					<icons.Time />
				</IconBadge>
				<ElementName>
					{translate(`training.script.tool.${toolType}.title`)}
				</ElementName>
				<RepresentativeText>
					{representativeQuestion
						? `“${representativeQuestion}”`
						: translate(`training.script.tool.${toolType}.description`)}
				</RepresentativeText>
				<Description>
					<intl.Translate>
						{isTrainer
							? 'script.archived.description.trainer'
							: 'script.archived.description.participant'}
					</intl.Translate>
				</Description>
				{onSeeInRecaps ? (
					<StageControls>
						<ui.ButtonPrimary onClick={onSeeInRecaps}>
							<intl.Translate>script.archived.cta.see_in_recaps</intl.Translate>
						</ui.ButtonPrimary>
					</StageControls>
				) : null}
			</Body>
		</Container>
	)
}

export default ArchivedActivityCard
